import { ACTION_CUSTOM } from '../constants';
import { AuthenticationScheme } from '../entities/credential.interface';
import {
  IConnectIntegrationWithCredentialInfo,
  getIntegrationTypeName,
  isCustomIntegrationTypeName,
} from '../entities/integration.interface';
import { IntegrationConnectInput } from '../types/action';
import { AuthenticatedConnectUser } from '../types/connect';
import { authSchemaRequiredInputs } from '../types/oauth';
import { DataType, KeyedSource } from '../types/resolvers';

import { IConnectUserContext } from './ConnectUserContext';

import { parseKeyedSource, stripUndefinedAndNull } from '.';


/**
 * this check if inputs needs to be shown before oauth
 * @param integration
 * @returns
 */
export const shouldShowPreAuthInputs = ({
  integration,
  authScheme,
  endUserSuppliedValues,
  isAccountTypeSupported,
}: {
  integration: IConnectIntegrationWithCredentialInfo;
  provider?: string;
  authScheme?: AuthenticationScheme;
  endUserSuppliedValues?: IntegrationConnectInput[];
  isAccountTypeSupported: boolean;
}): boolean => {
  const actionType = getIntegrationTypeName(integration);
  if (!actionType) {
    return false;
  }

  let inputs: IntegrationConnectInput[] | undefined;

  if (integration.customIntegration) {
    const customIntegration = integration.customIntegration;
    const authSpecificInputs = customIntegration.authenticationType
      ? authSchemaRequiredInputs[customIntegration.authenticationType]
      : [];
    inputs = [...authSpecificInputs, ...(customIntegration.inputFields ?? [])];
  } else if (authScheme === AuthenticationScheme.OAUTH) {
    inputs = endUserSuppliedValues;
  }

  /**
   * we show pre auth inputs in following cases
   * 1 . basic auth
   * 2. integration that support account type selection
   * 3. custom integration with input fields
   * 4. oauth integration with endUserSuppliedValues
   */
  return (
    Boolean(authScheme === AuthenticationScheme.BASIC) ||
    Boolean(inputs?.length) ||
    isAccountTypeSupported
  );
};

/**
 *  start oauth flow with new popup
 * @param param0
 * @returns
 */
export const startOAuthFlow = async ({
  context,
  integration,
  endUserSuppliedValues = {},
  authParams,
}: {
  context: IConnectUserContext;
  integration: IConnectIntegrationWithCredentialInfo;
  endUserSuppliedValues?: { [inputId: string]: string };
  authParams?: KeyedSource<DataType.ANY>[] | undefined;
}): Promise<void> => {
  const actionType = getIntegrationTypeName(integration);
  if (!actionType) {
    return;
  }

  const requestParameters: Record<string, string | boolean | undefined> = authParams
    ? parseKeyedSource(authParams)
    : {};

  const queryObj: Record<string, string> = stripUndefinedAndNull({
    action: actionType && isCustomIntegrationTypeName(actionType) ? ACTION_CUSTOM : actionType,
    endUserSuppliedValues: JSON.stringify(endUserSuppliedValues),
    parameters: JSON.stringify(requestParameters),
    userToken: (context.user as AuthenticatedConnectUser).token,
    integrationId: integration.id,
    redirectUrl: context.endUserIntegrationConfig?.overrideRedirectUrl,
    originForPostMessage: window.location.origin,
  }) as unknown as Record<string, string>;

  const url: string = `${context.environments.ZEUS_PUBLIC_URL}/projects/${
    context.projectId
  }/sdk/actions/build-oauth?${new URLSearchParams(queryObj).toString()}`;
  window.open(url, undefined, `width=${500},height=${600},left=${100},top=${150}`);
};
